import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import logo from "../Header/nav-logo.svg";
import { Carousel } from "react-responsive-carousel";
import { scroller } from "react-scroll";
import { Link } from "react-router-dom";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS / PURCHASE-FUNNEL SURFACE.** This is the hero of the
 * /free-trial landing page — the entry point of the trial signup funnel. The
 * conversion is styling-only: no handler, prop, condition, DOM node, image URL
 * or scroll target moved. Acceptance bar is visual parity.
 *
 * CONVENTIONS USED THROUGHOUT THIS BATCH
 * - Inside `styled()` the v4 `theme.spacing(n)` expressions are kept verbatim
 *   (raw emotion — no sx transforms run there).
 * - Inside `sx` every v4 `theme.spacing(n)` is written as the literal px string
 *   it produced (unit = 8). That is byte-identical AND immune to the two sx
 *   scaling traps in guide §2 (`top`/`right`/`bottom`/`left` are NOT spacing
 *   keys; `borderRadius` IS shape-scaled).
 * - `@media (min-width:600px)` / `(min-width:960px)` become
 *   `theme.breakpoints.up("sm"|"md")`; themeV5 pins the v4 breakpoint values so
 *   the emitted CSS text is identical. `@media (min-width:769px)` is NOT a
 *   breakpoint on this scale and is kept as the literal query it always was.
 * - Empty `@media` blocks (`"@media (min-width:960px)": {}`) are dropped:
 *   they declared nothing, so carrying them forward is noise, not a port
 *   (guide §1.2 row 1).
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `dashboardScrollerHalf` and
 * `noUnderline` were declared in the v4 `useStyles` object but never referenced
 * from the JSX, so they never rendered. Carrying them forward would be
 * inventing styles, not porting them.
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare host elements, which have no `sx` prop
 * ---------------------------------------------------------------------- */

// was: useStyles().background
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  width: "auto",
  display: "flex",
  boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.8)",
  height: "auto",
  paddingBottom: theme.spacing(0),
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
  },
  "@media (min-width:769px)": {
    height: "auto",
  },
  [theme.breakpoints.up("md")]: {
    height: "100vh",
  },
}));

// was: useStyles().logo
const Logo = styled("img")(({ theme }) => ({
  width: "40%",
  [theme.breakpoints.up("sm")]: {
    width: "200px",
  },
}));

// was: useStyles().skinWrapper
const SkinWrapper = styled("div")({
  position: "relative",
  marginBottom: "-3px",
});

// was: useStyles().appImage
const AppImage = styled("img")(({ theme }) => ({
  minHeight: "500px",
  maxHeight: "700px",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().appImageHalf
const AppImageHalf = styled("img")(({ theme }) => ({
  minHeight: "250px",
  maxHeight: "450px",
  marginBottom: theme.spacing(0),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().dashboardScroller
const DashboardScroller = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "64px",
  left: "-1px",
  height: "378px",
  width: "258px",
  padding: "7%",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    width: "296px",
    top: "95px",
    left: "22px",
    padding: "0",
    height: "580px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().loginBut
// `top`/`right` are literal px strings in v4 and stay literal strings here —
// those four sx keys are NOT spacing-scaled (guide §2), so a bare number would
// have meant px anyway, but the string removes all doubt.
const loginButSx = (theme: Theme) => ({
  position: "absolute",
  top: "15px",
  right: "10px",
  border: "2px solid #fff",
  color: "#fff",
  transition: "none",
  "&:hover": {
    border: "2px solid #ddd",
    color: "#ddd",
    transition: "none",
  },
  [theme.breakpoints.up("sm")]: {
    right: "15px",
  },
});

// was: useStyles().centerBoxLeft
const centerBoxLeftSx = (theme: Theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "80px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "80px",
  },
  // PARITY RESTORE — v4's `@media (min-width:769px) { height: auto }` is dropped,
  // not ported. In an `sx` object it cannot keep its v4 position: MUI's
  // `styleFunctionSx` seeds its output with an empty scaffold of the theme's
  // OWN breakpoint keys, so every `theme.breakpoints.*` block sits at the front
  // of the emitted object and any literal `@media` string is appended AFTER it.
  // 769 therefore landed after the 960 block and its `height: auto` beat
  // `height: 100vh` at every desktop width — measured on the built page at 1440:
  // the hero Grid item was 700px instead of 100vh (900px), and because the box
  // is `justifyContent: center` the whole hero rode ~98.5px up the page.
  // The block is provably inert in v4: below 769 it does not apply; from 769 to
  // 959 it re-asserts the `height: auto` the flat rule and the `sm` block
  // already set; from 960 up v4's later `md` block overrode it. Dropping it
  // reproduces v4 exactly at every width (guide §1.2 row 1).
  // NB the sibling `Background` styled() below keeps its 769 block verbatim —
  // `styled` is raw emotion with no scaffold, so source order survives there.
  [theme.breakpoints.up("md")]: {
    height: "100vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
});

// was: useStyles().centerBoxRight
const centerBoxRightSx = (theme: Theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "16px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "32px",
  },
  // PARITY RESTORE — same dropped-`@media (min-width:769px)` case as
  // `centerBoxLeftSx` above; see the note there for the measurement and proof.
  [theme.breakpoints.up("md")]: {
    height: "100vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
});

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  marginTop: "24px",
  letterSpacing: "-2px",
  fontSize: "5.4rem",
  lineHeight: "3rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  // PARITY RESTORE — the responsive `& span` rule is nested INSIDE the `& span`
  // block rather than left in the `sm` block where v4 declared it. Same
  // `styleFunctionSx` scaffold as the `centerBox*` note above: breakpoint keys
  // are emitted first, so a plain nested selector written at the top level of an
  // `sx` object comes out AFTER `@media { & span }` and, being an equal-
  // specificity (0,1,1) rule later in the sheet, wins at every width. Measured
  // on the built page at 1440: the "Free Trial" span rendered 48px/51.2px (the
  // xs values) instead of v4's 64px/67.2px — a 27% smaller subheading.
  // Nesting the breakpoint under the selector emits exactly v4's two rules in
  // v4's order — `.x span { 3rem }` then `@media (min-width:600px) { .x span
  // { 4rem } }` — so the cascade no longer depends on key order at all.
  "& span": {
    fontSize: "3rem",
    lineHeight: "3.2rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "4rem",
      lineHeight: "4.2rem",
    },
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "7.4rem",
    lineHeight: "3.6rem",
  },
});

// was: useStyles().button
// `color: "text.primary"` is NOT in the v4 rule — it restores a v4 default.
// v4's Button defaulted to `color="default"`, whose root style was
// `color: theme.palette.text.primary`. v5+ deleted the `default` colour and
// made `primary` the default, which here would paint the label lime on the
// lime `typography.button` background — i.e. invisible (guide §2, "Button
// default color"). Pinning the colour keeps the CTA rendering as it does today.
const buttonSx = (theme: Theme) => ({
  marginTop: "16px",
  width: "100%",
  fontSize: "1.2rem",
  padding: "16px",
  marginLeft: 0,
  marginRight: 0,
  color: "text.primary",
  [theme.breakpoints.up("sm")]: {
    width: "80%",
  },
});

// was: useStyles().buttonSubText
const buttonSubTextSx = (theme: Theme) => ({
  textAlign: "center",
  marginTop: "8px",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    textAlign: "left",
    marginLeft: "40px",
  },
});

// was: useStyles().imageContainer
const imageContainerSx = (theme: Theme) => ({
  height: "365px",
  [theme.breakpoints.up("sm")]: {
    height: "515px",
  },
});

const Top = () => {
  const theme = useTheme();

  // v4's <Hidden> was removed in v5+. Its default implementation was "js",
  // which UNMOUNTS the subtree rather than hiding it with CSS, so the faithful
  // port is useMediaQuery — with the same polarity (Hidden renders null when
  // its query matches).
  //
  // These two <Hidden>s are a COMPLEMENTARY PAIR (guide §2, T1/header): in v4
  // both were driven by a single `withWidth()` value, so exactly one branch
  // ever rendered — including with no matchMedia (jsdom / prerender), where
  // v4 fell through to the narrow branch. Two independent useMediaQuery calls
  // would each return false there and render BOTH images. So both booleans are
  // derived from ONE query.
  //
  // API delta: v4's `down(key)` meant "up to the START of the NEXT breakpoint",
  // so `xsDown` === `max-width: 599.95px` === `!up("sm")`.
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  function scrollToForm() {
    scroller.scrollTo("onboarding-form", {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
    });
  }

  return (
    <>
      <Background className="banner-bg">
        {/* Login button */}
        <Link to="/login" style={{ textDecoration: "none" }}>
          <Button variant="outlined" sx={loginButSx} size="small">
            Log In
          </Button>
        </Link>
        <Container maxWidth="md" fixed>
          {/* v5+ Grid: the `item` prop is gone and the breakpoint props are
              collapsed into `size`. This container has no `spacing`, so the
              computed widths are identical to v4's. */}
          <Grid container>
            <Grid size={{ xs: 12, md: 7 }} sx={centerBoxLeftSx}>
              <Box>
                <Box>
                  <Logo src={logo} alt="FarmFLiX Logo" />
                  <Typography variant="h1" sx={titleSx}>
                    72 Hour
                    <br />
                    <span>Free Trial</span>
                  </Typography>
                  {/* Whizz User to Try Stepper*/}
                  <Button onClick={scrollToForm} sx={buttonSx}>
                    Create a free account
                  </Button>
                  <Typography variant="body1" sx={buttonSubTextSx}>
                    No Payment Required for Free Trial
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={centerBoxRightSx}>
              <SkinWrapper>
                {/* Visible SM, MD, L, XL — was <Hidden xsDown> */}
                {smUp && (
                  <AppImage
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_100.png"
                    }
                    alt="FarmFLiX App"
                  />
                )}
                {/* Visible XS only — was <Hidden smUp> */}
                {!smUp && (
                  <AppImageHalf
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_75.png"
                    }
                    alt="FarmFLiX App"
                  />
                )}
                <DashboardScroller>
                  <Carousel
                    useKeyboardArrows={true}
                    showArrows={false}
                    interval={3000}
                    showThumbs={false}
                    infiniteLoop={true}
                    showStatus={false}
                    autoPlay={true}
                    showIndicators={false}
                    emulateTouch={false}
                    swipeable={false}
                    transitionTime={500}
                  >
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/1.png"
                        }
                        alt="App Dashboard"
                      />
                    </Box>
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/2.png"
                        }
                        alt="App Currently Playing"
                      />
                    </Box>
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/4.png"
                        }
                        alt="Clydes Farm Series"
                      />
                    </Box>
                  </Carousel>
                </DashboardScroller>
              </SkinWrapper>
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default Top;
